import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import { PackWithBuyButton } from '#src/components/commonCard/BuyPacks/PackWithBuyButton/index.tsx';
import { usePackAssets } from '#src/components/commonCard/PacksTypePage/usePackAssets.ts';

import { PremiumPackTitle } from './PremiumPackTitle/index.tsx';
import type { PremiumPack_cardPack } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 3, center: true })`
  position: relative;
  padding: var(--double-unit);
  width: 100%;
  background:
    linear-gradient(to bottom, transparent 0%, var(--c-black) 100%),
    var(--background-image) bottom -10% center/cover no-repeat;
  border-radius: var(--double-unit);
  --border-gradient: linear-gradient(
    to bottom,
    var(--league-color) 0%,
    transparent 20%
  );
  ${borderGradientStyle};
  overflow: hidden;
  justify-content: space-between;
`;

const StyledNeonEdge = styled(NeonEdge)`
  position: absolute;
  top: -1px;
  height: var(--double-unit);
  width: 200%;
  transform: rotate(180deg);
`;

type Props = {
  cardPack: PremiumPack_cardPack;
  cardPackGroupId: string;
  children?: ReactNode;
};
export const PremiumPack = ({ cardPack, cardPackGroupId, children }: Props) => {
  const { mobile: bgImage } = usePackAssets({
    cardPackGroupId: idFromObject(cardPackGroupId),
  });

  return (
    <Root
      style={
        {
          '--background-image': `url(${bgImage})`,
          '--league-color': cardPack.primaryColor,
        } as CSSProperties
      }
    >
      <StyledNeonEdge
        color={cardPack.primaryColor}
        showHalo="always"
        showNeon="always"
      />
      <PremiumPackTitle cardPack={cardPack} />
      <PackWithBuyButton
        cardPack={cardPack}
        cardPackGroupId={cardPackGroupId}
        gap={3}
        isPremiumPack
      />
      {children}
    </Root>
  );
};

PremiumPack.fragments = {
  cardPack: gql`
    fragment PremiumPack_cardPack on CardPack {
      slug
      primaryColor: color(variant: PRIMARY)
      ...PremiumPackTitle_cardPack
      ...PackWithBuyButton_cardPack
    }
    ${PackWithBuyButton.fragments.cardPack}
    ${PremiumPackTitle.fragments.cardPack}
  ` as TypedDocumentNode<PremiumPack_cardPack>,
};
